import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft, Building2, Users, Plus, ChevronDown, ChevronRight, RefreshCw, Trash2, X, Edit2 } from 'lucide-react'
import CreateEvent from '../components/CreateEvent'
import ErrorBoundary from '../components/ErrorBoundary'
import MCSBuilder from '../components/MCSBuilder'
import setThemeColor from '../lib/themeColor'
import cache from '../lib/localCache'
import { mutate } from '../lib/dbMutate'
import { isPlatformStaff } from '../lib/roleHelpers'

// ── CIPHER STYLE TOKENS ──────────────────────────────────────
const C = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    color: '#00ff66',
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 4px rgba(0,255,102,.5)',
    position: 'relative',
  },
  nav: {
    background: '#0a140a',
    borderBottom: '1px solid rgba(0,255,102,.18)',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 10,
  },
  display: { fontFamily: "'VT323', 'Share Tech Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  dim: { color: '#4aaa5a' },
  deep: { color: '#2a6a3a' },
  panel: {
    background: 'rgba(0, 20, 8, .4)',
    border: '1px solid rgba(0,255,102,.18)',
    boxShadow: '0 0 6px rgba(0,255,102,.08)',
    position: 'relative',
  },
  btnPrimary: {
    fontFamily: "'VT323', monospace",
    fontSize: '14px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    background: '#00ff66',
    color: '#000000',
    border: 'none',
    padding: '6px 14px',
    cursor: 'pointer',
    boxShadow: '0 0 6px #00ff66',
    textShadow: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  btnNav: {
    color: '#4aaa5a',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textShadow: '0 0 4px rgba(0,255,102,.3)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  tag: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', textShadow: 'none' },
}

const orgRoleTag = (role) => {
  switch(role) {
    case 'org_owner':  return { background: 'rgba(255,204,51,.08)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)' }
    case 'org_admin':  return { background: 'rgba(154,102,255,.12)', color: '#b380ff', border: '1px solid rgba(154,102,255,.3)' }
    case 'org_gm':     return { background: 'rgba(77,214,255,.10)', color: '#4dd6ff', border: '1px solid rgba(77,214,255,.3)' }
    case 'org_member': return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
    default:           return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
  }
}

const ORG_ROLE_OPTIONS = ['org_owner','org_admin','org_gm','org_member']

// OrgSettings — fog-of-war and play-sheet controls. Read organizations.features
// once, write merged updates so we never clobber other keys an upstream
// migration might add. Owner/admin only — read-only for everyone else.
function OrgSettings({ org, canWrite }) {
  const [features, setFeatures] = useState(org?.features || {})
  const [saving, setSaving] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)

  useEffect(() => { setFeatures(org?.features || {}) }, [org?.id])

  const REVEAL_OPTIONS = [
    { value: 'immediate', label: 'Immediate (full AAR at close)' },
    { value: '24_hours',  label: '24 hours' },
    { value: '7_days',    label: '7 days (default)' },
    { value: '14_days',   label: '14 days' },
    { value: '30_days',   label: '30 days' },
    { value: 'never',     label: 'Never (faction-only forever)' },
  ]
  const PLAY_SHEET_OPTIONS = [
    { value: 'limited', label: 'Co-GMs see next item only (default)' },
    { value: 'all',     label: 'Co-GMs see full faction timeline' },
    { value: 'off',     label: 'Co-GMs cannot view the play sheet' },
  ]

  const updateKey = async (key, value) => {
    if (!canWrite) return
    const next = { ...features, [key]: value }
    setFeatures(next)  // optimistic
    setSaving(true)
    const { error } = await supabase
      .from('organizations')
      .update({ features: next })
      .eq('id', org.id)
    setSaving(false)
    if (error) {
      console.warn('[OrgSettings] update failed:', error)
      setFeatures(features)  // revert
    } else {
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1200)
    }
  }

  const lblStyle = { ...C.display, fontSize: 11, color: '#4aaa5a', letterSpacing: '0.14em' }
  const helpStyle = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#2a6a3a', marginTop: 4 }
  const selectStyle = {
    background: '#0a140a', color: '#00ff66',
    border: '1px solid rgba(0,255,102,.3)',
    padding: '6px 10px',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
    width: '100%', maxWidth: 360,
  }

  return (
    <div className="mt-4" style={{ borderTop: '1px solid rgba(0,255,102,.12)', paddingTop: 12 }}>
      <div className="flex items-center justify-between mb-2">
        <p style={{ ...C.deep, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          ORG SETTINGS
        </p>
        {savedFlash && <span style={{ fontSize: 10, color: '#00ff66' }}>● SAVED</span>}
        {saving && <span style={{ fontSize: 10, color: '#ffcc33' }}>● SAVING...</span>}
      </div>

      <div className="space-y-3">
        <div>
          <p style={lblStyle}>AAR FACTION REVEAL</p>
          <select value={features.aar_faction_reveal || '7_days'}
                  disabled={!canWrite}
                  onChange={e => updateKey('aar_faction_reveal', e.target.value)}
                  style={{ ...selectStyle, marginTop: 4, opacity: canWrite ? 1 : 0.6 }}>
            {REVEAL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <p style={helpStyle}>How long after an event closes before players can see the OTHER faction's AAR data. GMs always see everything.</p>
        </div>

        <label className="flex items-start gap-3" style={{ padding: '8px 0' }}>
          <input type="checkbox"
                 checked={!!features.hide_faction_scores}
                 disabled={!canWrite}
                 onChange={e => updateKey('hide_faction_scores', e.target.checked)}
                 style={{ marginTop: 3, accentColor: '#00ff66' }} />
          <div style={{ flex: 1 }}>
            <p style={lblStyle}>HIDE FACTION SCORES FROM PLAYERS</p>
            <p style={helpStyle}>Players will see their actions (kills, missions, intel) but no point totals or faction rankings. GMs and m55_admin always see full scores.</p>
          </div>
        </label>

        <div>
          <p style={lblStyle}>PLAY SHEET — CO-GM ACCESS</p>
          <select value={features.play_sheet_mode || 'limited'}
                  disabled={!canWrite}
                  onChange={e => updateKey('play_sheet_mode', e.target.value)}
                  style={{ ...selectStyle, marginTop: 4, opacity: canWrite ? 1 : 0.6 }}>
            {PLAY_SHEET_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <p style={helpStyle}>Full GMs always see the full play sheet. This setting only restricts co-GMs.</p>
        </div>

        {/* ── Checkpoint mechanic — collapsed by default since most events
            won't use it; toggling cp_enabled lights up the rest. */}
        <div style={{ borderTop: '1px solid rgba(0,255,102,.12)', paddingTop: 12, marginTop: 4 }}>
          <label className="flex items-start gap-3" style={{ padding: '4px 0' }}>
            <input type="checkbox"
                   checked={!!features.cp_enabled}
                   disabled={!canWrite}
                   onChange={e => updateKey('cp_enabled', e.target.checked)}
                   style={{ marginTop: 3, accentColor: '#4dd6ff' }} />
            <div style={{ flex: 1 }}>
              <p style={lblStyle}>ENABLE CHECKPOINT CAPTURE (ANUBIS-CP)</p>
              <p style={helpStyle}>Players can scan checkpoint QRs to capture them for their faction. Configure capture timing + scoring below.</p>
            </div>
          </label>

          {features.cp_enabled && (
            <div className="space-y-3" style={{ marginTop: 8, paddingLeft: 24, borderLeft: '1px solid rgba(77,214,255,.18)' }}>
              <div>
                <p style={lblStyle}>CAPTURE MODE</p>
                <select value={features.cp_capture_mode || 'instant'}
                        disabled={!canWrite}
                        onChange={e => updateKey('cp_capture_mode', e.target.value)}
                        style={{ ...selectStyle, marginTop: 4 }}>
                  <option value="instant">Instant — single scan captures</option>
                  <option value="contested">Contested — must hold for the timer</option>
                </select>
              </div>

              <div>
                <p style={lblStyle}>CONTEST MODE (when other faction scans during capture)</p>
                <select value={features.cp_contest_mode || 'auto'}
                        disabled={!canWrite}
                        onChange={e => updateKey('cp_contest_mode', e.target.value)}
                        style={{ ...selectStyle, marginTop: 4 }}>
                  <option value="auto">Auto — opposing scan flips the capture</option>
                  <option value="deliberate">Deliberate — both timers pause until next scan</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p style={lblStyle}>CAPTURE DURATION (sec)</p>
                  <input type="number" min={5} max={3600}
                         value={features.cp_capture_duration_seconds ?? 300}
                         disabled={!canWrite}
                         onChange={e => updateKey('cp_capture_duration_seconds', parseInt(e.target.value) || 300)}
                         style={{ ...selectStyle, marginTop: 4 }} />
                </div>
                <div>
                  <p style={lblStyle}>POINTS ON CAPTURE</p>
                  <input type="number" min={0} max={5000}
                         value={features.cp_points_on_capture ?? 100}
                         disabled={!canWrite}
                         onChange={e => updateKey('cp_points_on_capture', parseInt(e.target.value) || 0)}
                         style={{ ...selectStyle, marginTop: 4 }} />
                </div>
                <div>
                  <p style={lblStyle}>POINTS PER HOLD INTERVAL</p>
                  <input type="number" min={0} max={1000}
                         value={features.cp_points_per_hold ?? 0}
                         disabled={!canWrite}
                         onChange={e => updateKey('cp_points_per_hold', parseInt(e.target.value) || 0)}
                         style={{ ...selectStyle, marginTop: 4 }} />
                </div>
                <div>
                  <p style={lblStyle}>HOLD INTERVAL (sec)</p>
                  <input type="number" min={30} max={3600}
                         value={features.cp_hold_interval_seconds ?? 300}
                         disabled={!canWrite}
                         onChange={e => updateKey('cp_hold_interval_seconds', parseInt(e.target.value) || 300)}
                         style={{ ...selectStyle, marginTop: 4 }} />
                </div>
              </div>

              <label className="flex items-start gap-3" style={{ padding: '4px 0' }}>
                <input type="checkbox"
                       checked={!!features.cp_reinforce_enabled}
                       disabled={!canWrite}
                       onChange={e => updateKey('cp_reinforce_enabled', e.target.checked)}
                       style={{ marginTop: 3, accentColor: '#4dd6ff' }} />
                <div style={{ flex: 1 }}>
                  <p style={lblStyle}>REINFORCEMENT REDUCES TIMER</p>
                  <p style={helpStyle}>Same-faction scans during capture chop seconds off the remaining timer (capped).</p>
                </div>
              </label>

              {features.cp_reinforce_enabled && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p style={lblStyle}>REDUCTION (sec per scan)</p>
                    <input type="number" min={1} max={300}
                           value={features.cp_reinforce_reduction_seconds ?? 30}
                           disabled={!canWrite}
                           onChange={e => updateKey('cp_reinforce_reduction_seconds', parseInt(e.target.value) || 30)}
                           style={{ ...selectStyle, marginTop: 4 }} />
                  </div>
                  <div>
                    <p style={lblStyle}>MAX REDUCTIONS PER CAPTURE</p>
                    <input type="number" min={1} max={20}
                           value={features.cp_reinforce_max_reductions ?? 5}
                           disabled={!canWrite}
                           onChange={e => updateKey('cp_reinforce_max_reductions', parseInt(e.target.value) || 5)}
                           style={{ ...selectStyle, marginTop: 4 }} />
                  </div>
                </div>
              )}

              <div>
                <p style={lblStyle}>CHECKPOINT VISIBILITY</p>
                <select value={features.cp_visibility || 'full'}
                        disabled={!canWrite}
                        onChange={e => updateKey('cp_visibility', e.target.value)}
                        style={{ ...selectStyle, marginTop: 4 }}>
                  <option value="full">Full — everyone sees the SITREP card</option>
                  <option value="limited">Limited — GMs + co-GMs only</option>
                  <option value="hidden">Hidden — full GMs only</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {!canWrite && (
          <p style={{ ...helpStyle, color: '#ff7a18' }}>Read-only — only org owners and admins can change these.</p>
        )}
      </div>
    </div>
  )
}

function Brackets({ children, style = {}, className = '' }) {
  return (
    <div className={className} style={{ ...C.panel, ...style }}>
      <div style={{ position:'absolute',top:-1,left:-1,width:8,height:8,borderTop:'2px solid #4aaa5a',borderLeft:'2px solid #4aaa5a' }}/>
      <div style={{ position:'absolute',bottom:-1,right:-1,width:8,height:8,borderBottom:'2px solid #4aaa5a',borderRight:'2px solid #4aaa5a' }}/>
      {children}
    </div>
  )
}

// ── ANU-200 — DOCTRINE TAB ───────────────────────────────────────────────────
// Org-scoped FRAGO and bounty template library. Visible to org_gm+.
// Each template is instantiated into real fragos/bounties via the ANU-201 DEPLOY flow.

const DOCTRINE_SCAN_TYPES = [
  { value: 'player_scan',     label: 'Player Scan' },
  { value: 'equipment_scan',  label: 'Equipment Scan' },
  { value: 'checkpoint_scan', label: 'Checkpoint Scan' },
  { value: 'kill_target',     label: 'Kill Target' },
  { value: 'timed_hold',      label: 'Timed Hold' },
  { value: 'manual',          label: 'Manual' },
]

const blankDoctrineObjective = () => ({
  title: '', scan_type: 'player_scan', is_fail_condition: false, fail_faction_scope: 'opposing',
})

const DI = {
  label:  { color: '#4aaa5a', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4, display: 'block', fontFamily: "'JetBrains Mono', monospace" },
  input:  { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: 'none' },
  select: { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 },
  row:    { display: 'flex', flexDirection: 'column', gap: 4 },
}

function DoctrineObjectiveRow({ obj, idx, onChange, onRemove }) {
  return (
    <div style={{ background: 'rgba(0,20,8,.4)', border: '1px solid rgba(0,255,102,.12)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ color: '#2a6a3a', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", minWidth: 20 }}>#{idx + 1}</span>
        <input
          style={{ ...DI.input, flex: 1 }}
          value={obj.title}
          onChange={e => onChange({ ...obj, title: e.target.value })}
          placeholder="Objective title"
        />
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', color: '#ff6a6a', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
          <X size={12} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, paddingLeft: 26 }}>
        <select style={DI.select} value={obj.scan_type} onChange={e => onChange({ ...obj, scan_type: e.target.value })}>
          {DOCTRINE_SCAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          style={{ ...DI.select, opacity: obj.is_fail_condition ? 1 : 0.35 }}
          value={obj.fail_faction_scope}
          disabled={!obj.is_fail_condition}
          onChange={e => onChange({ ...obj, fail_faction_scope: e.target.value })}
        >
          <option value="opposing">Fail scope: Opposing</option>
          <option value="any">Fail scope: Any</option>
        </select>
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 26, cursor: 'pointer' }}>
        <input type="checkbox" checked={obj.is_fail_condition}
          onChange={e => onChange({ ...obj, is_fail_condition: e.target.checked })}
          style={{ accentColor: '#ff7a3d' }} />
        <span style={{ ...DI.label, marginBottom: 0, color: '#ff9a3d' }}>FAIL CONDITION</span>
      </label>
    </div>
  )
}

function DoctrineTab({ orgId, userId }) {
  const [fragoTemplates, setFragoTemplates] = useState(null)
  const [bountyTemplates, setBountyTemplates] = useState(null)
  // mode: 'list' | 'new_frago' | 'edit_frago' | 'new_bounty' | 'edit_bounty'
  const [mode, setMode] = useState('list')
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)

  // FRAGO form state
  const [fName, setFName] = useState('')
  const [fDesc, setFDesc] = useState('')
  const [fObjs, setFObjs] = useState([blankDoctrineObjective()])
  const [fFactionScope, setFFactionScope] = useState('')
  const [fPhaseScoped, setFPhaseScoped] = useState(true)
  const [fPersistPost, setFPersistPost] = useState(false)

  // Bounty form state
  const [bName, setBName] = useState('')
  const [bTitle, setBTitle] = useState('')
  const [bDesc, setBDesc] = useState('')
  const [bDuration, setBDuration] = useState(0)
  const [bPhaseScoped, setBPhaseScoped] = useState(true)
  const [bPersistPost, setBPersistPost] = useState(false)
  const [bTargetType, setBTargetType] = useState('cadre_queue')

  useEffect(() => { fetchAll() }, [orgId])

  const fetchAll = async () => {
    const [ft, bt] = await Promise.all([
      supabase.from('frago_templates').select('*').eq('org_id', orgId).order('created_at'),
      supabase.from('bounty_templates').select('*').eq('org_id', orgId).order('created_at'),
    ])
    setFragoTemplates(ft.data || [])
    setBountyTemplates(bt.data || [])
  }

  const openNewFrago = () => {
    setFName(''); setFDesc(''); setFObjs([blankDoctrineObjective()])
    setFFactionScope(''); setFPhaseScoped(true); setFPersistPost(false)
    setEditingId(null); setMode('new_frago')
  }

  const openEditFrago = (t) => {
    setFName(t.name); setFDesc(t.description || '')
    setFObjs(t.objectives?.length ? t.objectives : [blankDoctrineObjective()])
    setFFactionScope(t.default_faction_scope || '')
    setFPhaseScoped(t.default_lifecycle_flags?.phase_scoped ?? true)
    setFPersistPost(t.default_lifecycle_flags?.persist_post_frago ?? false)
    setEditingId(t.id); setMode('edit_frago')
  }

  const saveFrago = async () => {
    if (!fName.trim()) { alert('Template name is required.'); return }
    setSaving(true)
    const payload = {
      org_id: orgId,
      name: fName.trim(),
      description: fDesc.trim() || null,
      objectives: fObjs.filter(o => o.title.trim()),
      default_faction_scope: fFactionScope || null,
      default_lifecycle_flags: { phase_scoped: fPhaseScoped, persist_post_frago: fPersistPost },
      is_platform_template: false,
      created_by: userId,
      updated_at: new Date().toISOString(),
    }
    if (editingId) {
      await supabase.from('frago_templates').update(payload).eq('id', editingId)
    } else {
      await supabase.from('frago_templates').insert(payload)
    }
    setSaving(false); setMode('list'); fetchAll()
  }

  const deleteFrago = async (t) => {
    if (!window.confirm(`Delete FRAGO doctrine "${t.name}"? This cannot be undone.`)) return
    await supabase.from('frago_templates').delete().eq('id', t.id)
    fetchAll()
  }

  const openNewBounty = () => {
    setBName(''); setBTitle(''); setBDesc('')
    setBDuration(0); setBPhaseScoped(true); setBPersistPost(false); setBTargetType('cadre_queue')
    setEditingId(null); setMode('new_bounty')
  }

  const openEditBounty = (t) => {
    setBName(t.name); setBTitle(t.title || ''); setBDesc(t.description || '')
    setBDuration(t.default_duration ?? 0)
    setBPhaseScoped(t.default_lifecycle_flags?.phase_scoped ?? true)
    setBPersistPost(t.default_lifecycle_flags?.persist_post_frago ?? false)
    setBTargetType(t.target_type || 'cadre_queue')
    setEditingId(t.id); setMode('edit_bounty')
  }

  const saveBounty = async () => {
    if (!bName.trim()) { alert('Template name is required.'); return }
    setSaving(true)
    const payload = {
      org_id: orgId,
      name: bName.trim(),
      title: bTitle.trim() || null,
      description: bDesc.trim() || null,
      default_duration: parseInt(bDuration, 10) || 0,
      default_lifecycle_flags: { phase_scoped: bPhaseScoped, persist_post_frago: bPersistPost },
      target_type: bTargetType,
      is_platform_template: false,
      created_by: userId,
      updated_at: new Date().toISOString(),
    }
    if (editingId) {
      await supabase.from('bounty_templates').update(payload).eq('id', editingId)
    } else {
      await supabase.from('bounty_templates').insert(payload)
    }
    setSaving(false); setMode('list'); fetchAll()
  }

  const deleteBounty = async (t) => {
    if (!window.confirm(`Delete BOUNTY doctrine "${t.name}"? This cannot be undone.`)) return
    await supabase.from('bounty_templates').delete().eq('id', t.id)
    fetchAll()
  }

  const updateObj = (idx, next) => setFObjs(prev => prev.map((o, i) => i === idx ? next : o))
  const removeObj = (idx) => setFObjs(prev => prev.filter((_, i) => i !== idx))

  const isFragoMode  = mode === 'new_frago'  || mode === 'edit_frago'
  const isBountyMode = mode === 'new_bounty' || mode === 'edit_bounty'
  const isEditing    = mode === 'edit_frago' || mode === 'edit_bounty'

  const listHeader = (label, onNew) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ ...DI.label, marginBottom: 0 }}>{label}</span>
      {mode === 'list' && (
        <button onClick={onNew} style={{ ...C.btnNav, color: '#00ff66', fontSize: 10 }}>
          <Plus size={11} /> NEW
        </button>
      )}
    </div>
  )

  const templateCard = (t, onEdit, onDelete) => (
    <div key={t.id} style={{ background: 'rgba(0,20,8,.35)', border: '1px solid rgba(0,255,102,.12)', padding: '8px 10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ ...DI.label, marginBottom: 2, color: '#00ff66' }}>{t.name}</p>
        {t.description && <p style={{ fontSize: 10, color: '#4aaa5a', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{t.description}</p>}
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button onClick={() => onEdit(t)} style={{ background: 'none', border: '1px solid rgba(0,255,102,.2)', color: '#4aaa5a', cursor: 'pointer', padding: '3px 6px' }}>
          <Edit2 size={10} />
        </button>
        <button onClick={() => onDelete(t)} style={{ background: 'none', border: '1px solid rgba(255,100,100,.2)', color: '#ff6a6a', cursor: 'pointer', padding: '3px 6px' }}>
          <Trash2 size={10} />
        </button>
      </div>
    </div>
  )

  const emptyState = (label) => (
    <p style={{ fontSize: 10, color: '#2a6a3a', fontFamily: "'JetBrains Mono', monospace" }}>No {label} — click NEW to create one</p>
  )

  // FRAGO form
  const fragoForm = (
    <div style={{ background: 'rgba(0,20,8,.5)', border: '1px solid rgba(0,255,102,.2)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ ...DI.label, marginBottom: 0, color: '#9aff9a' }}>{isEditing ? 'EDIT FRAGO DOCTRINE' : '+ NEW FRAGO DOCTRINE'}</span>
        <button onClick={() => setMode('list')} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer' }}><X size={12} /></button>
      </div>
      <div style={DI.row}>
        <label style={DI.label}>NAME *</label>
        <input style={DI.input} value={fName} onChange={e => setFName(e.target.value)} placeholder="Down Pilot" />
      </div>
      <div style={DI.row}>
        <label style={DI.label}>DESCRIPTION</label>
        <input style={DI.input} value={fDesc} onChange={e => setFDesc(e.target.value)} placeholder="Brief description..." />
      </div>
      <div style={DI.row}>
        <label style={DI.label}>DEFAULT FACTION SCOPE</label>
        <input style={DI.input} value={fFactionScope} onChange={e => setFFactionScope(e.target.value)} placeholder="e.g. BLUE (optional)" />
      </div>
      <div style={DI.row}>
        <label style={DI.label}>OBJECTIVES</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {fObjs.map((obj, idx) => (
            <DoctrineObjectiveRow key={idx} obj={obj} idx={idx}
              onChange={next => updateObj(idx, next)}
              onRemove={() => removeObj(idx)} />
          ))}
          <button type="button" onClick={() => setFObjs(p => [...p, blankDoctrineObjective()])}
            style={{ ...C.btnNav, fontSize: 10, justifyContent: 'flex-start' }}>
            <Plus size={10} /> ADD OBJECTIVE
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={fPhaseScoped} onChange={e => setFPhaseScoped(e.target.checked)} style={{ accentColor: '#00ff66' }} />
          <span style={{ ...DI.label, marginBottom: 0 }}>PHASE SCOPED</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={fPersistPost} onChange={e => setFPersistPost(e.target.checked)} style={{ accentColor: '#00ff66' }} />
          <span style={{ ...DI.label, marginBottom: 0 }}>PERSIST POST-FRAGO</span>
        </label>
      </div>
      <button onClick={saveFrago} disabled={saving}
        style={{ ...C.btnPrimary, opacity: saving ? 0.5 : 1, justifyContent: 'center' }}>
        {saving ? 'SAVING...' : isEditing ? 'SAVE CHANGES' : 'CREATE DOCTRINE'}
      </button>
    </div>
  )

  // Bounty form
  const bountyForm = (
    <div style={{ background: 'rgba(0,20,8,.5)', border: '1px solid rgba(0,255,102,.2)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ ...DI.label, marginBottom: 0, color: '#9aff9a' }}>{isEditing ? 'EDIT BOUNTY DOCTRINE' : '+ NEW BOUNTY DOCTRINE'}</span>
        <button onClick={() => setMode('list')} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer' }}><X size={12} /></button>
      </div>
      <div style={DI.row}>
        <label style={DI.label}>NAME *</label>
        <input style={DI.input} value={bName} onChange={e => setBName(e.target.value)} placeholder="Standard Kill Bounty" />
      </div>
      <div style={DI.row}>
        <label style={DI.label}>TITLE (shown to players)</label>
        <input style={DI.input} value={bTitle} onChange={e => setBTitle(e.target.value)} placeholder="Kill the HVT" />
      </div>
      <div style={DI.row}>
        <label style={DI.label}>DESCRIPTION</label>
        <input style={DI.input} value={bDesc} onChange={e => setBDesc(e.target.value)} placeholder="Brief description..." />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={DI.row}>
          <label style={DI.label}>DEFAULT DURATION (min)</label>
          <input type="number" min={0} style={DI.input} value={bDuration} onChange={e => setBDuration(e.target.value)} placeholder="0 = no limit" />
        </div>
        <div style={DI.row}>
          <label style={DI.label}>TARGET TYPE</label>
          <select style={DI.select} value={bTargetType} onChange={e => setBTargetType(e.target.value)}>
            <option value="cadre_queue">Cadre Queue</option>
            <option value="open">Open</option>
            <option value="specific">Specific</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={bPhaseScoped} onChange={e => setBPhaseScoped(e.target.checked)} style={{ accentColor: '#00ff66' }} />
          <span style={{ ...DI.label, marginBottom: 0 }}>PHASE SCOPED</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={bPersistPost} onChange={e => setBPersistPost(e.target.checked)} style={{ accentColor: '#00ff66' }} />
          <span style={{ ...DI.label, marginBottom: 0 }}>PERSIST POST-FRAGO</span>
        </label>
      </div>
      <button onClick={saveBounty} disabled={saving}
        style={{ ...C.btnPrimary, opacity: saving ? 0.5 : 1, justifyContent: 'center' }}>
        {saving ? 'SAVING...' : isEditing ? 'SAVE CHANGES' : 'CREATE DOCTRINE'}
      </button>
    </div>
  )

  if (fragoTemplates === null) {
    return <p style={{ fontSize: 10, color: '#2a6a3a', fontFamily: "'JetBrains Mono', monospace" }}>Loading doctrine...</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* FRAGO TEMPLATES */}
      <div>
        {listHeader('FRAGO TEMPLATES', openNewFrago)}
        {isFragoMode
          ? fragoForm
          : fragoTemplates.length === 0
            ? emptyState('FRAGO doctrines')
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {fragoTemplates.map(t => templateCard(t, openEditFrago, deleteFrago))}
              </div>
        }
      </div>

      {/* BOUNTY TEMPLATES */}
      <div style={{ borderTop: '1px solid rgba(0,255,102,.08)', paddingTop: 12 }}>
        {listHeader('BOUNTY TEMPLATES', openNewBounty)}
        {isBountyMode
          ? bountyForm
          : bountyTemplates.length === 0
            ? emptyState('BOUNTY doctrines')
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {bountyTemplates.map(t => templateCard(t, openEditBounty, deleteBounty))}
              </div>
        }
      </div>
    </div>
  )
}

export default function OrgAdmin() {
  const { user, profile } = useAuth()
  const [orgs, setOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  // ANU-204 — viewingOrg = selected org entry ({ org_role, organizations }).
  // null means show the multi-org selection screen.
  const [viewingOrg, setViewingOrg] = useState(null)
  const [activeOrgTab, setActiveOrgTab] = useState('overview')
  const [members, setMembers] = useState({})
  const [orgEvents, setOrgEvents] = useState({})
  const [showCreateEvent, setShowCreateEvent] = useState(false)
  const [createEventOrg, setCreateEventOrg] = useState(null)
  const [message, setMessage] = useState('')
  const [addingStaffOrg, setAddingStaffOrg] = useState(null)
  const [newStaffEmail, setNewStaffEmail] = useState('')
  const [newStaffRole, setNewStaffRole] = useState('org_member')

  useEffect(() => { setThemeColor('cipher') }, [])

  useEffect(() => { fetchMyOrgs() }, [user])

  const fetchMyOrgs = async () => {
    if (!user) return; setLoading(true)
    // ANU-46 — platform staff get every org without needing an
    // org_members row anywhere. Their platform_role is god mode for
    // the admin surface: they can land here, expand any org, and
    // manage members without first being added to each customer org.
    if (isPlatformStaff(profile)) {
      const { data: all } = await supabase
        .from('organizations')
        .select('*')
        .order('name')
      const list = (all || []).map(o => ({ org_role: 'm55_platform', organizations: o }))
      if (list.length === 0) { window.location.href = '/dashboard'; return }
      setOrgs(list); setLoading(false)
      if (list.length === 1) selectOrg(list[0])
      return
    }

    // /org should surface every org the user has standing in:
    //   1. any org_members row (any role — staff included), AND
    //   2. any org they own directly via organizations.owner_id, even if no
    //      org_members row exists for them.
    const [memberRes, ownedRes] = await Promise.all([
      supabase.from('org_members').select('org_role, organizations(*)').eq('user_id', user.id),
      supabase.from('organizations').select('*').eq('owner_id', user.id),
    ])

    const byId = {}
    for (const e of (memberRes.data || [])) {
      if (e.organizations) byId[e.organizations.id] = { org_role: e.org_role, organizations: e.organizations }
    }
    for (const o of (ownedRes.data || [])) {
      if (!byId[o.id]) byId[o.id] = { org_role: 'org_owner', organizations: o }
    }
    const list = Object.values(byId)

    if (list.length === 0) { window.location.href = '/dashboard'; return }
    setOrgs(list); setLoading(false)
    if (list.length === 1) selectOrg(list[0])
  }

  const selectOrg = async (entry) => {
    setViewingOrg(entry)
    setActiveOrgTab('overview')
    setMessage('')
    const orgId = entry.organizations.id
    if (!members[orgId]) fetchMembers(orgId)
  }

  const fetchMembers = async (orgId) => {
    // ANU-46 — pull platform_role on the joined user so we can hide
    // M55 staff from the org-side staff list.
    const { data } = await supabase
      .from('org_members')
      .select('*, user:users(id, display_name, callsign, email, platform_role, role)')
      .eq('org_id', orgId)
    const filtered = (data || []).filter(m => !(m.user?.platform_role || m.user?.role === 'm55_admin'))
    setMembers(prev => ({ ...prev, [orgId]: filtered }))
  }

  const fetchOrgEvents = async (orgId) => {
    const { data } = await supabase
      .from('events')
      .select('id, name, status, starts_at, ends_at, created_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(30)
    setOrgEvents(prev => ({ ...prev, [orgId]: data || [] }))
  }

  // ── STAFF MANAGEMENT ─────────────────────────────────────────
  const addStaff = async (orgId) => {
    if (!newStaffEmail.trim()) return
    const { data: found } = await supabase
      .from('users')
      .select('id, display_name, callsign, email, platform_role, role')
      .ilike('email', newStaffEmail.trim())
      .limit(1)
    if (!found?.[0]) { setMessage('No user with that email'); return }
    // ANU-46 — block adding M55 platform staff as org members.
    if (found[0].platform_role || found[0].role === 'm55_admin') {
      setMessage('M55 platform staff already have access — no org-membership row needed.')
      return
    }
    const userId = found[0].id
    const label = found[0].callsign || found[0].display_name || found[0].email

    const { data: existing } = await supabase
      .from('org_members')
      .select('id, org_role')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .limit(1)

    if (existing?.[0]) {
      const current = existing[0].org_role
      if (current === newStaffRole) {
        setMessage(`${label} is already on staff as ${current}`)
        return
      }
      if (!window.confirm(`${label} is already on staff as "${current}". Change role to "${newStaffRole}"?`)) return
      const { error: upErr } = await supabase
        .from('org_members')
        .update({ org_role: newStaffRole })
        .eq('id', existing[0].id)
      if (upErr) { setMessage('Update failed: ' + upErr.message); return }
      setMessage(`${label}: ${current} → ${newStaffRole}`)
    } else {
      const { error } = await supabase
        .from('org_members')
        .insert({ org_id: orgId, user_id: userId, org_role: newStaffRole })
      if (error) { setMessage('Failed: ' + error.message); return }
      setMessage(`${label} added as ${newStaffRole}`)
    }

    setNewStaffEmail('')
    setNewStaffRole('org_member')
    setAddingStaffOrg(null)
    await fetchMembers(orgId)
  }

  const removeStaff = async (orgId, memberId, label) => {
    if (!window.confirm(`Remove ${label || 'this member'} from staff?`)) return
    // Track 2.4 / Bug 50 — server-side trigger blocks pulling M55
    // platform staff out of an org via this handler. Surface the
    // friendly error rather than the raw 42501 RLS message.
    const result = await mutate('org_members', () =>
      supabase.from('org_members').delete().eq('id', memberId)
    )
    if (result.error) {
      setMessage(result.friendlyMessage || 'Remove failed.')
      return
    }
    setMessage('Removed')
    await fetchMembers(orgId)
  }

  if (loading) {
    return (
      <div style={C.page} className="flex items-center justify-center">
        <p style={C.dim}>LOADING<span style={{ display: 'inline-block', width: 8, height: 14, background: '#00ff66', boxShadow: '0 0 6px #00ff66', verticalAlign: 'middle', marginLeft: 4, animation: 'cipher-blink 1s steps(2) infinite' }} /></p>
        <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>
      </div>
    )
  }

  // ── Shared: Create Event modal ────────────────────────────────────────────
  const createEventModal = showCreateEvent && createEventOrg && (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg">
        <ErrorBoundary>
          <CreateEvent
            user={profile}
            profile={profile}
            onClose={() => { setShowCreateEvent(false); setCreateEventOrg(null) }}
            onCreated={async () => {
              setShowCreateEvent(false); setCreateEventOrg(null); setMessage('Event created')
              await cache.clear('events').catch(() => {})
              await cache.clear('event_players').catch(() => {})
              if (viewingOrg) fetchOrgEvents(viewingOrg.organizations.id)
            }}
          />
        </ErrorBoundary>
      </div>
    </div>
  )

  // ── ANU-204: Multi-org selection screen ──────────────────────────────────
  if (!viewingOrg) {
    return (
      <div style={C.page}>
        <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>
        {createEventModal}
        <nav style={C.nav}>
          <div className="flex items-center gap-4">
            <button onClick={() => window.location.href = '/dashboard'} style={C.btnNav}>
              <ArrowLeft size={14} /> BACK
            </button>
            <span style={{ ...C.display, fontSize: '16px', color: '#00ff66', textShadow: '0 0 6px rgba(0,255,102,.6)' }}>
              {'>_'} ORG ADMIN
            </span>
          </div>
          <button onClick={fetchMyOrgs} style={C.btnNav}><RefreshCw size={14} /></button>
        </nav>
        <main className="max-w-2xl mx-auto p-6" style={{ position: 'relative', zIndex: 10 }}>
          <h2 className="text-xl font-bold mb-1" style={C.display}>SELECT ORGANIZATION</h2>
          <p className="text-sm mb-6" style={C.dim}>{orgs.length} organization{orgs.length !== 1 ? 's' : ''}</p>
          {message && <div className="mb-4 p-3 text-sm" style={{ ...C.panel, color: '#00ff66' }}>{message}</div>}
          <div className="space-y-2">
            {orgs.map(entry => {
              const org = entry.organizations
              if (!org) return null
              const myRole = entry.org_role
              return (
                <Brackets key={org.id} className="overflow-hidden">
                  <button onClick={() => selectOrg(entry)} className="w-full p-4 flex items-center gap-3 text-left"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00ff66' }}>
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold"
                      style={{ background: '#00ff66', color: '#000', textShadow: 'none', fontFamily: "'VT323', monospace", fontSize: '18px' }}>
                      {org.name[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate" style={C.display}>{org.name}</p>
                        <span style={{ ...C.tag, ...orgRoleTag(myRole) }}>{myRole}</span>
                      </div>
                      {org.description && <p className="text-xs mt-0.5 truncate" style={C.dim}>{org.description}</p>}
                    </div>
                    <ChevronRight size={16} style={C.dim} className="flex-shrink-0" />
                  </button>
                </Brackets>
              )
            })}
          </div>
        </main>
      </div>
    )
  }

  // ── ANU-204: Org detail view with tabs ───────────────────────────────────
  const org = viewingOrg.organizations
  const myRole = viewingOrg.org_role
  const orgId = org.id
  const orgMembers = members[orgId] || []
  const canManageStaff    = ['org_owner', 'org_admin', 'm55_platform'].includes(myRole)
  const canManageDoctrine = ['org_owner', 'org_admin', 'org_gm', 'm55_platform'].includes(myRole)
  const isAddingHere = addingStaffOrg === orgId

  const ORG_TABS = [
    { id: 'overview',  label: 'OVERVIEW' },
    { id: 'members',   label: 'MEMBERS' },
    { id: 'events',    label: 'EVENTS' },
    ...(canManageDoctrine ? [{ id: 'doctrine', label: 'DOCTRINE' }] : []),
    ...(canManageDoctrine ? [{ id: 'settings', label: 'SETTINGS' }] : []),
  ]

  const tabSt = (active) => ({
    fontFamily: "'VT323', monospace", fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
    background: active ? '#00ff66' : 'rgba(0,20,8,.4)', color: active ? '#000' : '#4aaa5a',
    border: active ? 'none' : '1px solid rgba(0,255,102,.18)',
    padding: '8px 14px', cursor: 'pointer', textShadow: 'none',
  })

  const eventsForOrg = orgEvents[orgId]

  const statusColor = (s) => {
    if (s === 'live') return '#00ff66'
    if (s === 'prep' || s === 'registration') return '#ffcc33'
    if (s === 'closed' || s === 'cancelled') return '#4aaa5a'
    return '#9aa6b0'
  }

  return (
    <div style={C.page}>
      <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>
      {createEventModal}

      <nav style={C.nav}>
        <div className="flex items-center gap-4">
          {orgs.length > 1 ? (
            <button onClick={() => { setViewingOrg(null); setMessage('') }} style={C.btnNav}>
              <ArrowLeft size={14} /> ORGS
            </button>
          ) : (
            <button onClick={() => window.location.href = '/dashboard'} style={C.btnNav}>
              <ArrowLeft size={14} /> BACK
            </button>
          )}
          <span style={{ ...C.display, fontSize: '16px', color: '#00ff66', textShadow: '0 0 6px rgba(0,255,102,.6)' }}>
            {org.name}
          </span>
          <span style={{ ...C.tag, ...orgRoleTag(myRole) }}>{myRole}</span>
        </div>
        <button onClick={fetchMyOrgs} style={C.btnNav}><RefreshCw size={14} /></button>
      </nav>

      <main className="max-w-2xl mx-auto p-4" style={{ position: 'relative', zIndex: 10 }}>
        {message && <div className="mb-3 p-3 text-sm" style={{ ...C.panel, color: '#00ff66' }}>{message}</div>}

        {/* ── TABS ── */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {ORG_TABS.map(t => (
            <button key={t.id} onClick={() => {
              setActiveOrgTab(t.id)
              if (t.id === 'events' && !eventsForOrg) fetchOrgEvents(orgId)
            }} style={tabSt(activeOrgTab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeOrgTab === 'overview' && (
          <div className="space-y-4">
            <Brackets className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center font-bold"
                  style={{ background: '#00ff66', color: '#000', textShadow: 'none', fontFamily: "'VT323', monospace", fontSize: '22px' }}>
                  {org.name[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p style={{ ...C.display, fontSize: 18, color: '#00ff66' }}>{org.name}</p>
                  {org.description && <p className="text-sm mt-1" style={C.dim}>{org.description}</p>}
                </div>
              </div>
            </Brackets>
            <button onClick={() => { setCreateEventOrg(org); setShowCreateEvent(true) }} style={C.btnPrimary}>
              <Plus size={12} /> NEW EVENT
            </button>
          </div>
        )}

        {/* ── MEMBERS TAB ── */}
        {activeOrgTab === 'members' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p style={{ ...C.display, fontSize: 14 }}>STAFF ({orgMembers.length})</p>
              {canManageStaff && !isAddingHere && (
                <button onClick={() => { setAddingStaffOrg(orgId); setNewStaffEmail(''); setNewStaffRole('org_member') }}
                  style={{ ...C.btnNav, color: '#00ff66' }}>
                  <Plus size={12} /> ADD STAFF
                </button>
              )}
            </div>

            {isAddingHere && (
              <div className="mb-3 p-3 space-y-2" style={{ background: 'rgba(0,20,8,.5)', border: '1px solid rgba(0,255,102,.18)' }}>
                <div className="flex items-center justify-between">
                  <p style={{ ...C.display, fontSize: 11, color: '#4aaa5a', letterSpacing: '0.14em' }}>ADD STAFF MEMBER</p>
                  <button onClick={() => { setAddingStaffOrg(null); setNewStaffEmail('') }}
                    style={{ ...C.btnNav, color: '#4aaa5a' }}><X size={12} /></button>
                </div>
                <input type="email" autoFocus value={newStaffEmail}
                  onChange={e => setNewStaffEmail(e.target.value)}
                  placeholder="email@example.com"
                  style={{ background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, width: '100%' }} />
                <select value={newStaffRole} onChange={e => setNewStaffRole(e.target.value)}
                  style={{ background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, width: '100%' }}>
                  {ORG_ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => addStaff(orgId)} disabled={!newStaffEmail.trim()}
                  style={{ ...C.btnPrimary, opacity: newStaffEmail.trim() ? 1 : 0.4, width: '100%', justifyContent: 'center' }}>
                  <Plus size={12} /> ADD
                </button>
              </div>
            )}

            {orgMembers.length === 0 ? (
              <p className="text-sm" style={C.deep}>No staff members</p>
            ) : (
              <div className="space-y-1">
                {orgMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 gap-2" style={{ background: 'rgba(0,20,8,.3)' }}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{m.user?.callsign || m.user?.display_name || m.user?.email}</p>
                      <p className="text-xs truncate" style={C.deep}>{m.user?.email}</p>
                    </div>
                    <span style={{ ...C.tag, ...orgRoleTag(m.org_role) }}>{m.org_role}</span>
                    {canManageStaff && m.user_id !== user?.id && (
                      <button onClick={() => removeStaff(orgId, m.id, m.user?.callsign || m.user?.display_name || m.user?.email)}
                        title="Remove staff"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff7a18', padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── EVENTS TAB ── */}
        {activeOrgTab === 'events' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p style={{ ...C.display, fontSize: 14 }}>EVENTS</p>
              <div className="flex gap-2">
                <button onClick={() => fetchOrgEvents(orgId)} style={C.btnNav}><RefreshCw size={12} /></button>
                <button onClick={() => { setCreateEventOrg(org); setShowCreateEvent(true) }} style={C.btnPrimary}>
                  <Plus size={12} /> NEW EVENT
                </button>
              </div>
            </div>
            {!eventsForOrg ? (
              <p style={{ ...C.dim, fontSize: 12 }}>Loading…</p>
            ) : eventsForOrg.length === 0 ? (
              <p style={{ ...C.dim, fontSize: 12 }}>No events yet — create one above.</p>
            ) : (
              <div className="space-y-2">
                {eventsForOrg.map(ev => (
                  <div key={ev.id} className="flex items-center justify-between px-3 py-2 gap-2" style={{ background: 'rgba(0,20,8,.35)', border: '1px solid rgba(0,255,102,.12)' }}>
                    <div className="min-w-0 flex-1">
                      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#00ff66' }}>{ev.name}</p>
                      {ev.starts_at && (
                        <p style={{ fontSize: 10, color: '#4aaa5a', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                          {new Date(ev.starts_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <span style={{ ...C.tag, background: 'rgba(0,20,8,.4)', color: statusColor(ev.status), border: `1px solid ${statusColor(ev.status)}40` }}>
                      {ev.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── DOCTRINE TAB ── org_gm+ only (gated by ORG_TABS) */}
        {activeOrgTab === 'doctrine' && canManageDoctrine && (
          <div>
            <p style={{ ...C.display, fontSize: 14, marginBottom: 16 }}>DOCTRINE</p>
            <DoctrineTab orgId={orgId} userId={user?.id} />
          </div>
        )}

        {/* ── SETTINGS TAB ── org_gm+ only (gated by ORG_TABS) */}
        {activeOrgTab === 'settings' && canManageDoctrine && (
          <div className="space-y-6">
            <p style={{ ...C.display, fontSize: 14, marginBottom: 4 }}>ORG SETTINGS</p>
            {canManageStaff && (
              <div style={{ borderBottom: '1px solid rgba(0,255,102,.12)', paddingBottom: 16, marginBottom: 16 }}>
                <p className="text-xs mb-2" style={{ ...C.deep, letterSpacing: '0.12em', textTransform: 'uppercase' }}>MCS TEMPLATES</p>
                <MCSBuilder orgId={orgId} userId={user?.id} />
              </div>
            )}
            <OrgSettings org={org} canWrite={canManageStaff} />
          </div>
        )}
      </main>
    </div>
  )
}
